<template>
    <div class="product-list-page">

        <!-- 1. 顶部：产品类目 (Category) -->
        <div class="sub-nav">
            <div class="sub-item" v-for="category in categoryList" :key="category.id"
                :class="{ active: activeCategoryId === category.id }" @click="changeCategory(category.id)">
                <!-- 优先使用接口返回的图片，如果没有则用本地图片或者占位 -->
                <img class="sub-icon" :src="category.icon" />

                <span>{{ $t(`product.nav.` + category.name) }}</span>
            </div>
        </div>

        <main class="main-container">
            <!-- 2. 顶部产品主视觉 (Hero) -->
            <!-- <section class="product-hero">
                <div class="banner">
                    <img src="@/assets/products/banner.png" loading="lazy" class="background-img" alt="Banner"
                        onerror="this.style.display='none'">
                    <div class="banner-placeholder"></div>
                </div>
            </section> -->

            <!-- 3. 主体左右分栏布局 -->
            <section class="layout-wrapper">
                <!-- 左侧：产品类型 (Type/Model) -->
                <aside class="sidebar">
                    <div class="category-group">
                        <h3 class="category-title active">
                            {{ $t(`product.nav.` + currentCategoryName) }}
                        </h3>
                        <ul class="sub-category-list" v-if="typeList.length > 0">
                            <li v-for="type in typeList" :key="type.id" :class="{ 'active': activeTypeId === type.id }"
                                @click="changeType(type.id)">
                                {{ $t(`product.sub.` + type.name) }}
                            </li>
                        </ul>
                        <div v-else class="empty-text">
                            {{ $t('product.common.emptyProduct') }}
                        </div>
                    </div>
                </aside>

                <!-- 右侧：产品展示与详细内容 -->
                <article class="content-area">
                    <!-- 分类标题与描述 -->
                    <div class="category-header">
                        <h1>
                            {{ $t(`product.nav.` + currentCategoryName) }}
                            <span v-if="activeTypeId">- {{ $t(`product.sub.` + currentTypeName) }}</span>
                        </h1>
                    </div>

                    <!-- 产品网格 (直接使用接口返回的产品列表) -->
                    <div class="product-grid" v-if="productList.length > 0">
                        <div class="product-card" v-for="product in productList" :key="product.id">
                            <div class="card-img" @click="toProductDetail(product.id)">
                                <!-- 替换为接口返回的产品主图 -->
                                <img style="height: 100%;width: 100%;object-fit: contain;"
                                    :src="`https://qiniu-au.wifigsmalarm.com/images/` + product.icon + `.png`" />
                            </div>
                            <h4 class="card-title">{{ product.title || product.name }}</h4>
                            <p class="c ard-desc">{{ locale === 'zh' ? '￥' + (product.price * 6.8).toFixed(2) : 'US$' +
                                product.price.toFixed(2) }}</p>
                            <div class="card-actions">
                                <button class="btn-outline" @click="toProductDetail(product.id)">{{
                                    $t('product.common.learnMore') }}</button>
                                <button class="btn-outline text-cyan" @click="addToCart(product)">
                                    {{ $t('product.common.addToCart') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 缺省状态 -->
                    <div class="empty-state" v-else>
                        <p>{{ $t('product.common.emptyProduct') }}</p>
                    </div>
                </article>
            </section>
        </main>

        <!-- ================= 详情弹窗 ================= -->
        <transition name="fade">
            <div class="modal-overlay" v-if="isModalOpen" @click="closeModal">
                <!-- 加上 @click.stop 防止点击弹窗内部时触发了外层蒙层的关闭事件 -->
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <button class="close-btn" @click="closeModal">×</button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-desc rich-text-container" v-html="currentProduct.description"></div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn-outline text-cyan" @click="addToCartFromModal">
                            {{ $t('product.common.addToCart') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <!-- ================= 全局 Loading 遮罩 ================= -->
        <transition name="fade">
            <div class="global-loading" v-if="isSubmitting">
                <div class="spinner"></div>
                <p>Processing...</p>
            </div>
        </transition>
        <!-- ================= 购物车功能区 ================= -->

        <!-- 悬浮购物车入口 -->
        <div class="floating-cart" @click="isCartOpen = true">
            <span class="cart-icon">🛒</span>
            <span class="cart-badge" v-if="totalItems > 0">{{ totalItems }}</span>
        </div>

        <!-- 购物车抽屉蒙层 -->
        <div class="cart-overlay" v-if="isCartOpen" @click="isCartOpen = false"></div>

        <!-- 购物车侧边抽屉 -->
        <div class="cart-drawer" :class="{ 'open': isCartOpen }">
            <div class="cart-header">
                <div class="header-left">
                    <button class="back-btn" v-if="cartStep === 2" @click="cartStep = 1">←</button>
                    <h2>{{ cartStep === 1 ? $t('product.cart.titleList') : $t('product.cart.titleForm') }}</h2>
                </div>
                <button class="close-btn" @click="isCartOpen = false">×</button>
            </div>

            <div class="cart-body">
                <!-- Step 1: 产品列表与数量调整 -->
                <div v-if="cartStep === 1">
                    <div v-if="cartList.length === 0" class="empty-cart">
                        <p>{{ $t('product.cart.empty') }}</p>
                    </div>
                    <div v-else class="cart-list">
                        <div class="cart-item" v-for="(item, index) in cartList" :key="item.id">
                            <div class="item-img">
                                <img style="height: 100%;width: 100%;object-fit: contain;"
                                    :src="item.icon.startsWith('http') ? item.icon : `https://qiniu-au.wifigsmalarm.com/images/` + item.icon + `.png`" />
                                <span>{{ locale === 'zh' ? '￥' + (item.price * item.rate).toFixed(2) : 'US$' +
                                    item.price.toFixed(2) }}</span>
                            </div>

                            <div class="item-info">
                                <h4>{{ item.title || item.name }}</h4>
                                <p>{{ item.typeName ? $t(`product.nav.` + item.typeName) : '' }} <span
                                        v-if="item.subTypeName">- {{
                                            $t(`product.sub.` + item.subTypeName) }}</span></p>
                                <!-- 数量控制 -->
                                <div class="qty-control">
                                    <button @click="decreaseQty(index)">-</button>
                                    <input type="number" v-model.number="item.quantity" min="1" readonly />
                                    <button @click="increaseQty(index)">+</button>
                                </div>
                            </div>
                            <button class="remove-btn" @click="removeFromCart(index)">{{ $t('product.cart.delete')
                                }}</button>
                        </div>
                    </div>
                </div>

                <!-- Step 2: 客户信息表单 -->
                <div v-if="cartStep === 2" class="checkout-form">
                    <!-- 蜜罐字段 -->
                    <div style="opacity: 0; position: absolute; top: 0; left: 0; height: 0; width: 0; z-index: -1;">
                        <input type="text" v-model="honeypot" tabindex="-1" autocomplete="off" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.name') }} <span>*</span></label>
                        <input type="text" v-model="formData.name" :placeholder="$t('product.form.namePlaceholder')"
                            maxlength="50" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.location') }} <span>*</span></label>
                        <input type="text" v-model="formData.area" :placeholder="$t('product.form.locationPlaceholder')"
                            maxlength="100" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.address') }}</label>
                        <input type="text" v-model="formData.address"
                            :placeholder="$t('product.form.addressPlaceholder')" maxlength="200" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.email') }}</label>
                        <input type="text" v-model="formData.email" :placeholder="$t('product.form.emailPlaceholder')"
                            maxlength="100" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.whatsapp') }}</label>
                        <input type="text" v-model="formData.whatsapp"
                            :placeholder="$t('product.form.whatsappPlaceholder')" maxlength="30" />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('product.form.remark') }}</label>
                        <textarea v-model="formData.remark" style="height: 120px;"
                            :placeholder="$t('product.form.remarkPlaceholder')" maxlength="500"></textarea>
                    </div>

                    <!-- Cloudflare Turnstile 验证组件 -->
                    <TurnstileWidget ref="turnstileRef" sitekey="你的Cloudflare站点Key" @verify="onTurnstileSuccess"
                        @expire="turnstileToken = ''" @error="turnstileToken = ''" />
                </div>
            </div>

            <div class="cart-footer">
                <!-- Step 1 底部按钮 -->
                <template v-if="cartStep === 1">
                    <h3>{{ totalPrice }}</h3>
                    <div class="cart-total">{{ $t('product.cart.totalItems', { count: totalItems }) }}</div>
                    <button class="btn-submit" :disabled="cartList.length === 0" @click="cartStep = 2">
                        {{ $t('product.cart.nextStep') }}
                    </button>
                </template>

                <!-- Step 2 底部按钮 -->
                <template v-if="cartStep === 2">
                    <button class="btn-submit" @click="submitApplication">
                        {{ $t('product.cart.submit') }}
                    </button>
                </template>
            </div>
        </div>
        <!-- ================= 提交成功/系统提示弹窗 ================= -->
        <transition name="fade">
            <div class="modal-overlay" v-if="isMessageModalOpen" @click="closeMessageModal">
                <div class="modal-content message-modal" @click.stop>
                    <div class="message-icon" :class="messageType">
                        <!-- 成功图标 -->
                        <svg v-if="messageType === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <!-- 警告图标 -->
                        <svg v-if="messageType === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <h3 class="message-title">{{ messageTitle }}</h3>
                    <p class="message-text">{{ messageText }}</p>
                    <button class="btn-primary btn-block" @click="closeMessageModal">
                        {{ $t('product.alert.confirm') || 'OK' }}
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TypeList, ModelList, List, Order } from '@/api/product'
import { useRouter } from 'vue-router'

import TurnstileWidget from '@/components/TurnstileWidget.vue'

const turnstileRef = ref(null)
const turnstileToken = ref('')

// 接收验证通过生成的 Token
const onTurnstileSuccess = (token) => {
    turnstileToken.value = token
}

const router = useRouter()

const { t, locale } = useI18n()

// ================= 详情弹窗逻辑 =================
const isModalOpen = ref(false)
const currentProduct = ref('') // 存放当前点击的产品信息
const isSubmitting = ref(false)
// 打开弹窗
// const openModal = (product) => {
//     currentProduct.value = product
//     isModalOpen.value = true
// }

// 关闭弹窗
const closeModal = () => {
    isModalOpen.value = false
    // 稍微延迟清空数据，防止关闭动画执行时看到内容突然消失
    setTimeout(() => {
        currentProduct.value = {}
    }, 300)
}

// 可选优化：在弹窗里点击“加入购物车”后，加入成功并顺便关掉弹窗
const addToCartFromModal = () => {
    addToCart(currentProduct.value)
    closeModal()
}


// ================= 数据结构与状态管理 =================
const categoryList = ref([]) // 接口返回的数据：顶级类目 (Top Nav)
const typeList = ref([])     // 接口返回的数据：产品类型 (Sidebar)
const productList = ref([])  // 接口返回的数据：产品列表 (Grid)

const activeCategoryId = ref('')
const activeTypeId = ref('')

onMounted(() => {
    // 页面加载完成立即请求顶级类目
    getCategoryData()
})

const toProductDetail = (id) => {
    router.push('/product/detail/' + id)
}

// 1. 获取顶级类目
const getCategoryData = async () => {
    try {
        const result = await TypeList()
        // 修复原代码 Bug：必须用 .value 赋值才能触发响应式更新
        categoryList.value = result || []
        // 如果有数据，默认选中第一项
        if (categoryList.value.length > 0) {
            changeCategory(categoryList.value[0].id)
        }
    } catch (error) {
        console.error('获取类目失败：', error)
    }
}

// 2. 根据选中的类目，获取对应的类型
const getTypeData = async (categoryId) => {
    try {
        const params = { 'type_id': categoryId }
        const result = await ModelList(params)
        typeList.value = result || []

        if (typeList.value.length > 0) {
            // 获取到类型后，默认选中第一个类型
            changeType(typeList.value[0].id)
        } else {
            // 如果该类目下没有类型，清空当前类型ID并直接去拉取产品数据
            activeTypeId.value = ''
            getProductData()
        }
    } catch (error) {
        console.error('获取类型失败：', error)
    }
}

// 3. 根基类目和类型，获取最终产品列表
const getProductData = async () => {
    try {
        const params = {
            'type_id': activeCategoryId.value,
            'model_id': activeTypeId.value
        }
        const result = await List(params)
        productList.value = result || []
    } catch (error) {
        console.error('获取产品失败：', error)
    }
}

// ================= 交互动作 =================

// 切换顶级类目
const changeCategory = (id) => {
    if (activeCategoryId.value === id) return
    activeCategoryId.value = id
    // 类目变化，去拉取下一级的类型
    getTypeData(id)
}

// 切换产品类型
const changeType = (id) => {
    if (activeTypeId.value === id) return
    activeTypeId.value = id
    // 类型变化，去拉取产品
    getProductData()
}

// 计算当前选中的类目和类型名称（用于展示在页面标题和购物车中）
const currentCategoryName = computed(() => {
    const cat = categoryList.value.find(c => c.id === activeCategoryId.value)
    return cat ? cat.name : ''
})

const currentTypeName = computed(() => {
    const type = typeList.value.find(t => t.id === activeTypeId.value)
    return type ? type.name : ''
})

// ================= 新增：系统提示弹窗逻辑 =================
const isMessageModalOpen = ref(false)
const messageType = ref('success') // 'success' 或 'warning'
const messageTitle = ref('')
const messageText = ref('')

const showMessage = (type, title, text) => {
    messageType.value = type
    messageTitle.value = title
    messageText.value = text
    isMessageModalOpen.value = true
}

const closeMessageModal = () => {
    isMessageModalOpen.value = false
}

// ================= 购物车与表单逻辑 =================

const cartList = ref([])
const isCartOpen = ref(false)
const cartStep = ref(1)

const formData = reactive({
    name: '',
    address: '',
    area: '',
    whatsapp: '',
    email: '',
    remark: '',
    zip_code: '',
    order_type: 0,
})

const totalItems = computed(() => {
    return cartList.value.reduce((sum, item) => sum + item.quantity, 0)
})

const totalPrice = computed(() => {
    const total = cartList.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
    return locale.value === 'zh' ? `￥${(total * 6.8).toFixed(2)}` : `$${total.toFixed(2)}`
})

const addToCart = (product) => {
    const existingItem = cartList.value.find(item => item.id === product.id)
    if (existingItem) {
        existingItem.quantity += 1
    } else {
        cartList.value.push({
            ...product,
            quantity: 1,
            // 附加当前的分类和类型名称，方便在购物车列表展示
            categoryName: currentCategoryName.value,
            typeName: currentTypeName.value
        })
    }
    isCartOpen.value = true
}

const increaseQty = (index) => {
    cartList.value[index].quantity++
}

const decreaseQty = (index) => {
    if (cartList.value[index].quantity > 1) {
        cartList.value[index].quantity--
    } else {
        removeFromCart(index)
    }
}

const removeFromCart = (index) => {
    cartList.value.splice(index, 1)
    if (cartList.value.length === 0) {
        cartStep.value = 1
    }
}

// ================= 新增：安全状态管理 =================
const lastSubmitTime = ref(0)
const submitCooldown = 5000 // 限制每次提交间隔至少 5 秒

// 蜜罐字段（机器人经常遍历表单填入所有 input，正常用户看不到也不填）
const honeypot = ref('')

// SQL 注入与危险字符特征正则 (匹配单引号闭合、联合查询、注释符号、脚本标签等)
const SQLI_REGEX = /('|"|;|--|\/\*|\*\/|union\s+select|select\s+.*from|insert\s+into|delete\s+from|drop\s+table|update\s+.*set|<script[\s\S]*?>)/i

// 基础文本过滤：剔除前后空白、危险闭合字符
const sanitizeInput = (str) => {
    if (typeof str !== 'string') return ''
    return str.trim()
}

// 检查是否存在明显的 SQL / 脚本注入攻击载荷
const containsDangerousPayload = (obj) => {
    for (const key in obj) {
        const val = obj[key]
        if (typeof val === 'string' && SQLI_REGEX.test(val)) {
            return true
        }
    }
    return false
}

// ================= 重构：提交逻辑 =================
const submitApplication = async () => {
    // 1. 防御：防重复点击/限流保护
    if (isSubmitting.value) return
    if (honeypot.value) return

    const now = Date.now()
    if (now - lastSubmitTime.value < submitCooldown) {
        const waitSeconds = Math.ceil((submitCooldown - (now - lastSubmitTime.value)) / 1000)
        showMessage('warning', t('product.alert.notice') || '提示', `操作太频繁，请 ${waitSeconds} 秒后再试`)
        return
    }

    // 2. 防御：蜜罐检测 (如果隐藏 input 有值，说明是被机器脚本批量填充)
    if (honeypot.value) {
        console.warn('Honeypot triggered')
        showMessage('error', 'Error', 'Invalid request')
        return
    }

    // 3. 基础必填校验
    const cleanName = sanitizeInput(formData.name)
    const cleanArea = sanitizeInput(formData.area)
    const cleanEmail = sanitizeInput(formData.email)
    const cleanWhatsapp = sanitizeInput(formData.whatsapp)
    const cleanZip = sanitizeInput(formData.zip_code)
    const cleanAddress = sanitizeInput(formData.address)
    const cleanRemark = sanitizeInput(formData.remark)

    if (!cleanName || !cleanArea) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.fillRequired'))
        return
    }

    // 邮箱与手机号格式严格校验（阻断在输入框塞入 SQL 语句）
    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        showMessage('warning', t('product.alert.notice'), '请输入正确的邮箱格式')
        return
    }

    if (cleanWhatsapp && !/^[+0-9\s-]{6,20}$/.test(cleanWhatsapp)) {
        showMessage('warning', t('product.alert.notice'), '请输入正确的联系电话')
        return
    }



    // 4. 防御：恶意字符与 SQL 语法特征检测
    const sanitizedForm = {
        name: cleanName.slice(0, 50),
        area: cleanArea.slice(0, 100),
        address: cleanAddress.slice(0, 200),
        email: cleanEmail.slice(0, 100),
        whatsapp: cleanWhatsapp.slice(0, 30),
        zip_code: cleanZip.slice(0, 20),
        remark: cleanRemark.slice(0, 500),
        order_type: Number(formData.order_type) || 0
    }

    if (containsDangerousPayload(sanitizedForm)) {
        showMessage('warning', t('product.alert.notice'), '输入内容包含非法或不支持的特殊字符，请检查后重新提交')
        return
    }

    // 5. 购物车合法性过滤（只取安全字段与数字类型，防止改写 payload 注入）
    const sanitizedList = cartList.value.map(item => ({
        id: Number(item.id),
        quantity: Math.max(1, parseInt(item.quantity) || 1),
        price: Number(item.price) || 0
    })).filter(item => !isNaN(item.id) && item.id > 0)

    if (sanitizedList.length === 0) {
        showMessage('warning', t('product.alert.notice'), '购物车中无有效商品')
        return
    }

    if (!turnstileToken.value) {
        showMessage('warning', t('product.alert.notice'), '请先完成安全验证')
        return
    }

    // 6. 构造并发送请求
    const finalData = {
        turnstile_token: turnstileToken.value,
        list: sanitizedList,
        ...sanitizedForm
    }

    isSubmitting.value = true
    lastSubmitTime.value = now

    try {
        await Order(finalData)
        showMessage('success', t('product.alert.success'), t('product.alert.submitSuccess'))

        // 成功后重置
        cartList.value = []
        Object.keys(formData).forEach(key => formData[key] = '')
        cartStep.value = 1
        isCartOpen.value = false
    } catch (error) {
        console.error('提交订单失败：', error)
        showMessage('warning', t('product.alert.notice'), error.message || '提交失败，请稍后重试')
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
/* =========================================
   全局与基础样式
========================================= */
.product-list-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #333;
    position: relative;
}

.empty-text {
    color: #999;
    font-size: 0.9rem;
    padding-left: 15px;
    margin-top: 10px;
}

.lang-switch-bar {
    position: absolute;
    top: 15px;
    right: 40px;
    z-index: 10;
}

.btn-lang {
    background: #f0f0f0;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
}

.btn-lang:hover {
    background: #e0e0e0;
}

.placeholder-img,
.placeholder-icon {
    background-color: #eaeaea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    border-radius: 4px;
}

/* =========================================
   Header & SubNav 样式
========================================= */
.sub-nav {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 100px;
    padding: 100px 40px 40px 40px;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
}

.sub-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    font-size: 0.85rem;
    color: #666;
    transition: all 0.2s;
}

.sub-item:hover {
    color: #00bcd4;
}

.sub-item.active {
    font-weight: bold;
    color: #000;
}

.sub-icon {
    width: 35px;
    height: 35px;
    margin-bottom: 8px;
    object-fit: contain;
}

/* =========================================
   页面主体布局
========================================= */
.main-container {
    width: 100%;
}

.product-hero {
    height: 40vh;
    min-height: 300px;
}

.banner {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #2a2a2a;
}

.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.layout-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    padding: 60px 40px;
    gap: 60px;
}

/* =========================================
   左侧：侧边栏分类
========================================= */
.sidebar {
    width: 200px;
    flex-shrink: 0;
    position: sticky;
    top: 60px;
    height: fit-content;
}

.category-group {
    margin-bottom: 20px;
}

.category-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 12px;
    cursor: pointer;
    color: #1a1a1a;
    transition: color 0.2s;
}

.category-title:hover {
    color: #00bcd4;
}

.category-title.active {
    color: #00bcd4;
}

.sub-category-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0 10px;
    border-left: 2px solid #eee;
}

.sub-category-list li {
    font-size: 0.9rem;
    color: #666;
    padding: 8px 0 8px 15px;
    cursor: pointer;
    transition: all 0.2s;
}

.sub-category-list li:hover {
    color: #000;
}

.sub-category-list li.active {
    color: #00bcd4;
    font-weight: 600;
}

/* =========================================
   右侧：内容区与产品网格
========================================= */
.content-area {
    flex: 1;
    min-width: 0;
}

.category-header {
    margin-bottom: 40px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 20px;
}

.category-header h1 {
    font-size: 2rem;
    margin: 0 0 10px 0;
}

.category-header p {
    color: #666;
    font-size: 1rem;
    margin: 0;
}

/* 产品网格 */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 30px;
    margin-bottom: 80px;
}

.product-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.product-card:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
    transform: translateY(-5px);
}

.card-img {
    width: 80%;
    aspect-ratio: 4/3;
    margin-bottom: 25px;
    cursor: pointer;
}

.card-title {
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0 0 10px 0;
}

.card-desc {
    font-size: 0.85rem;
    color: #888;
    line-height: 1.4;
    margin: 0 0 25px 0;
    height: 40px;
    overflow: hidden;
}

.card-actions {
    display: flex;
    gap: 10px;
    margin-top: auto;
    width: 100%;
    justify-content: center;
}

.btn-outline {
    padding: 8px 16px;
    border: 1px solid #d9d9d9;
    background: transparent;
    border-radius: 20px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    color: #333;
}

.btn-outline:hover {
    border-color: #999;
}

.btn-outline.text-cyan {
    color: #00bcd4;
    border-color: #00bcd4;
}

.btn-outline.text-cyan:hover {
    background: #00bcd4;
    color: #fff;
}

.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #999;
    font-size: 1.1rem;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 80px;
}

.bottom-info-section {
    border-top: 1px solid #eaeaea;
    padding-top: 40px;
}

/* =========================================
   购物车悬浮按钮与抽屉样式
========================================= */
.floating-cart {
    position: fixed;
    right: 40px;
    bottom: 80px;
    width: 60px;
    height: 60px;
    background-color: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    z-index: 99;
    transition: transform 0.2s ease;
}

.floating-cart:hover {
    transform: scale(1.05);
}

.cart-icon {
    font-size: 1.5rem;
}

.cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #ff4d4f;
    color: white;
    font-size: 0.75rem;
    font-weight: bold;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border: 2px solid #fff;
}

.cart-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
}

.cart-drawer {
    position: fixed;
    top: 0;
    right: -400px;
    width: 400px;
    height: 100vh;
    background: #fff;
    z-index: 101;
    display: flex;
    flex-direction: column;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
}

.cart-drawer.open {
    right: 0;
}

.cart-header {
    padding: 20px;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-left h2 {
    margin: 0;
    font-size: 1.2rem;
}

.back-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #666;
}

.back-btn:hover {
    color: #00bcd4;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: #999;
}

.close-btn:hover {
    color: #333;
}

.cart-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f9f9f9;
}

.empty-cart {
    text-align: center;
    color: #999;
    margin-top: 50px;
}

.cart-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.cart-item {
    background: #fff;
    padding: 15px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 15px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.02);
}

.item-img {
    width: 60px;
    height: 60px;
    /* background-color: #eee; */
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    color: #aaa;
    flex-shrink: 0;
    flex-direction: column;
}

.item-info {
    flex: 1;
}

.item-info h4 {
    margin: 0 0 5px 0;
    font-size: 1rem;
    color: #333;
}

.item-info p {
    margin: 0;
    font-size: 0.8rem;
    color: #666;
}

.qty-control {
    display: flex;
    align-items: center;
    margin-top: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: fit-content;
    overflow: hidden;
    margin-left: 25%;
}

.qty-control button {
    background: #f5f5f5;
    border: none;
    width: 28px;
    height: 28px;
    cursor: pointer;
    font-size: 1rem;
    color: #555;
}

.qty-control button:hover {
    background: #e0e0e0;
}

.qty-control input {
    width: 40px;
    height: 28px;
    border: none;
    text-align: center;
    font-size: 0.9rem;
    border-left: 1px solid #ddd;
    border-right: 1px solid #ddd;
    outline: none;
    background: #fff;
}

.remove-btn {
    background: none;
    border: none;
    color: #ff4d4f;
    cursor: pointer;
    font-size: 0.85rem;
}

.remove-btn:hover {
    text-decoration: underline;
}

.checkout-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    font-size: 0.9rem;
    color: #333;
    font-weight: 500;
}

.form-group label span {
    color: #ff4d4f;
}

.form-group input,
.form-group textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.95rem;
    outline: none;
}

.form-group input:focus,
.form-group textarea:focus {
    border-color: #00bcd4;
}

.cart-footer {
    padding: 20px;
    border-top: 1px solid #eaeaea;
    background: #fff;
}

.cart-total {
    margin-bottom: 15px;
    font-size: 0.95rem;
    color: #666;
    font-weight: bold;
}

.btn-submit {
    width: 100%;
    padding: 12px 0;
    background: #00bcd4;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s;
}

.btn-submit:hover:not(:disabled) {
    background: #00acc1;
}

.btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .layout-wrapper {
        flex-direction: column;
        padding: 20px;
    }

    .sidebar {
        width: 100%;
        position: static;
        margin-bottom: 30px;
    }

    .cart-drawer {
        width: 100%;
        right: -100%;
    }
}

/* =========================================
   全局 Loading 遮罩样式
========================================= */
.global-loading {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(5px);
    /* 现代毛玻璃效果 */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    /* 必须确保在最顶层 */
    color: #00bcd4;
    font-weight: 600;
}

/* 纯 CSS 旋转加载动画 */
.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e0e0e0;
    border-top: 4px solid #00bcd4;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* =========================================
   产品详情弹窗 (Modal) 美化版样式
========================================= */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.modal-content {
    background: #fff;
    width: 90%;
    max-width: 550px;
    /* 稍微放宽一点展示图文 */
    border-radius: 20px;
    /* 圆角变大，更现代 */
    padding: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    /* 更柔和的高级阴影，去掉了丑陋的实体边框 */
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #111;
    font-weight: 700;
}

/* 圆形灰色按钮，悬浮变深 */
.modal-header .close-btn {
    background: #f3f4f6;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.modal-header .close-btn:hover {
    background: #e5e7eb;
    color: #111;
}

.modal-body {
    flex: 1;
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 5px;
}

/* 滚动条美化 */
.modal-body::-webkit-scrollbar {
    width: 6px;
}

.modal-body::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
}

/* 弹窗图片容器 */
.modal-cover {
    width: 100%;
    height: 240px;
    background: #f9fafb;
    border-radius: 12px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
}

.modal-cover img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

/* 醒目的价格 */
.modal-price {
    font-size: 1.75rem;
    color: #ff4d4f;
    font-weight: 800;
    margin-bottom: 15px;
}

.modal-desc {
    font-size: 1rem;
    color: #4b5563;
    line-height: 1.7;
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;
    text-align: left;
}

.modal-footer {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
    gap: 15px;
}

/* 按钮美化 */
.btn-cancel,
.btn-primary {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-cancel {
    background: transparent;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.btn-cancel:hover {
    background: #f3f4f6;
    color: #111;
}

.btn-primary {
    background: #00bcd4;
    color: #fff;
}

.btn-primary:hover {
    background: #00acc1;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
}

/* 弹窗渐显动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active .modal-content,
.fade-leave-active .modal-content {
    transition: transform 0.3s ease cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from .modal-content,
.fade-leave-to .modal-content {
    transform: translateY(20px) scale(0.96);
}

/* =========================================
   系统提示弹窗 (Message Modal) 样式
========================================= */
.message-modal {
    max-width: 360px;
    /* 提示弹窗不需要太宽 */
    text-align: center;
    padding: 40px 30px;
    align-items: center;
}

.message-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.message-icon svg {
    width: 32px;
    height: 32px;
}

/* 成功状态配色：微弱的绿底 + 鲜艳的绿标 */
.message-icon.success {
    background-color: #d1fae5;
    color: #10b981;
}

/* 警告/必填状态配色：微弱的橙底 + 鲜艳的橙标 */
.message-icon.warning {
    background-color: #fef3c7;
    color: #f59e0b;
}

.message-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #111;
    margin: 0 0 10px 0;
}

.message-text {
    font-size: 1rem;
    color: #6b7280;
    margin: 0 0 30px 0;
    line-height: 1.5;
}

/* 让确定按钮撑满整行 */
.btn-block {
    width: 100%;
    padding: 14px 0;
    font-size: 1.05rem;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.modal-content {
    background: #fff;
    width: 90%;
    max-width: 550px;
    /* 稍微放宽一点展示图文 */
    border-radius: 20px;
    /* 圆角变大，更现代 */
    padding: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    /* 更柔和的高级阴影，去掉了丑陋的实体边框 */
    display: flex;
    flex-direction: column;
}
</style>